import { useEffect, useState } from 'react';
import type { TrialBalanceReport } from '@/core/models/reports.models';
import { notifyError } from '@/core/notify/notify';
import { reportsService } from '@/core/services/reports';
import { Icon } from '@/kit/Icon';
import { DialogClose, DialogContent, type DialogComponentProps } from '@/kit/dialog';
import './TrialBalancePreview.scss';

export interface TrialBalancePreviewData {
  trialBalanceId: string;
  /** #161: the report's comparative trial balance, shown under the current one when the report has one. */
  previousTrialBalanceId?: string | null;
}

/** Angular `number: '1.2-2'`. */
const twoDecimals = (value: number | null | undefined): string =>
  value === null || value === undefined
    ? ''
    : value.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * A quick, read-only look at the money behind a report's trial balance - the figures, not the PDF - reached from the
 * report list's "Trial Balance(s)" link, without leaving the list.
 */
export function TrialBalancePreview({ data }: DialogComponentProps<TrialBalancePreviewData, never>) {
  /** #161: every trial balance the report is built from, current first - the link lists them all. */
  const [reports, setReports] = useState<TrialBalanceReport[]>([]);
  const trialBalanceId = data?.trialBalanceId;
  const previousTrialBalanceId = data?.previousTrialBalanceId;

  useEffect(() => {
    const ids = [trialBalanceId, previousTrialBalanceId]
      .filter((id): id is string => !!id)
      .filter((id, index, all) => all.indexOf(id) === index);
    if (ids.length === 0) {
      return;
    }
    let cancelled = false;
    Promise.all(ids.map((id) => reportsService.getTrialBalanceReport(id))).then(
      (loaded) => {
        if (!cancelled) setReports(loaded);
      },
      (error) => {
        if (!cancelled) notifyError(error);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [trialBalanceId, previousTrialBalanceId]);

  return (
    <div className="mat-dialog-wrapper trial-balance-preview">
      <DialogContent>
        <div className="main-contain">
          <div className="sidenav-header">
            <div className="d-flex align-items-center justify-space-between">
              <h2>
                <span>Trial balance</span>
              </h2>
              <DialogClose>
                <Icon name="close" className="close" />
              </DialogClose>
            </div>
          </div>
        </div>

        {/* #161: the current trial balance, then the comparative one when the report uses it. */}
        {reports.map((report, index) => {
          const first = index === 0;
          return (
            <div key={report.trialBalanceId}>
              {first && <p className="font-size-18 fw-bold mb-10">{report.clientName}</p>}
              <p className={`fw-bold mb-10${first ? '' : ' mt-1'}`}>
                {report.refNo} ({first ? 'current' : 'comparative'})
              </p>
              <div className="mat-table-wrapper">
                <table className="w-100 tb-preview-table">
                  <thead>
                    <tr>
                      <th>Code</th>
                      <th>Account Name</th>
                      <th className="text-right" colSpan={2}>
                        {report.fromDate} - {report.toDate}
                      </th>
                    </tr>
                    <tr>
                      <th></th>
                      <th></th>
                      <th className="text-right">Debit</th>
                      <th className="text-right">Credit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.rows.map((row, rowIndex) => (
                      <tr key={rowIndex}>
                        <td>{row.code}</td>
                        <td>{row.name}</td>
                        <td className="text-right">{row.debit ? twoDecimals(row.debit) : '-'}</td>
                        <td className="text-right">{row.credit ? twoDecimals(row.credit) : '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="totalRow">
                      <td colSpan={2}>Total</td>
                      <td className="text-right">{twoDecimals(report.totalDebit)}</td>
                      <td className="text-right">{twoDecimals(report.totalCredit)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          );
        })}
      </DialogContent>
    </div>
  );
}
